import React, { useState } from 'react';
import { FaGift, FaRocket, FaTags, FaUserCheck } from 'react-icons/fa';
import familyImage from '../assets/familia-header.png';
import familyImageAvif from '../assets/familia-header.avif';
import familyImageWebp from '../assets/familia-header.webp';
import styles from './styles/JoinFamilyPage.module.css';
import SmartImage from '../components/SmartImage';
import { BRAND } from '../data/brand';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const JoinFamilyPage: React.FC = () => {
  const [formData, setFormData] = useState({
    dni: '',
    nombre: '',
    apellido: '',
    direccion: '',
    telefono: '',
    email: '',
    fecha_nacimiento: '',
    sexo: ''
  });
  const [status, setStatus] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus(null);
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/miembros/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(data?.error || 'No pudimos registrar tus datos.');
      }

      setStatus({ type: 'success', text: 'Listo, ya sos cliente fiel de ${BRAND.name}.' });
      setFormData({
        dni: '',
        nombre: '',
        apellido: '',
        direccion: '',
        telefono: '',
        email: '',
        fecha_nacimiento: '',
        sexo: ''
      });
    } catch (err: any) {
      setStatus({ type: 'error', text: err.message || 'Error al registrar cliente fiel.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles['join-container']}>
      <header className={styles['join-header']}>
        <div className={styles['image-wrapper']}>
          <SmartImage src={familyImage} avif={familyImageAvif} webp={familyImageWebp} alt="Forma parte de nuestra familia" />
        </div>
        <p className={styles['join-intro']}>
          Sumate a nuestra comunidad de clientes fieles para recibir beneficios, promociones y novedades.
        </p>
      </header>

      <section className={styles['benefits-section']}>
        <h2>Por que unirte</h2>
        <p className={styles['benefits-subtitle']}>
          Tus datos quedan guardados en la misma gestion de clientes fieles que usamos en el local.
        </p>

        <div className={styles['benefits-grid']}>
          <div className={styles['benefit-card']}>
            <div className={styles['benefit-icon']}><FaGift /></div>
            <h3>Regalos especiales</h3>
            <p>Recibi obsequios sorpresa en fechas especiales y por tu fidelidad.</p>
          </div>

          <div className={styles['benefit-card']}>
            <div className={styles['benefit-icon']}><FaTags /></div>
            <h3>Promociones personalizadas</h3>
            <p>Accede a descuentos y promos preparadas para nuestra comunidad.</p>
          </div>

          <div className={styles['benefit-card']}>
            <div className={styles['benefit-icon']}><FaRocket /></div>
            <h3>Novedades anticipadas</h3>
            <p>Enterate antes de nuevos productos, eventos y lanzamientos.</p>
          </div>
        </div>
      </section>

      <section className={styles['how-to-join-section']}>
        <div className={styles['cta-box']}>
          <div className={styles['form-title']}>
            <FaUserCheck />
            <h2>Registrarme como cliente fiel</h2>
          </div>
          <p>Completalo una sola vez. El sistema valida que no exista otro registro con el mismo DNI o email.</p>

          {status && (
            <div className={`${styles['status-msg']} ${styles[status.type]}`}>
              {status.text}
            </div>
          )}

          <form className={styles['loyal-form']} onSubmit={handleSubmit}>
            <div className={styles['form-row']}>
              <label>
                DNI
                <input name="dni" value={formData.dni} onChange={handleChange} required inputMode="numeric" />
              </label>
              <label>
                Email
                <input name="email" value={formData.email} onChange={handleChange} required type="email" />
              </label>
            </div>
            <div className={styles['form-row']}>
              <label>
                Nombre
                <input name="nombre" value={formData.nombre} onChange={handleChange} required autoComplete="given-name" />
              </label>
              <label>
                Apellido
                <input name="apellido" value={formData.apellido} onChange={handleChange} required autoComplete="family-name" />
              </label>
            </div>
            <div className={styles['form-row']}>
              <label>
                Telefono
                <input name="telefono" value={formData.telefono} onChange={handleChange} type="tel" autoComplete="tel" />
              </label>
              <label>
                Fecha de nacimiento
                <input name="fecha_nacimiento" value={formData.fecha_nacimiento} onChange={handleChange} type="date" />
              </label>
            </div>
            <label>
              Direccion
              <input name="direccion" value={formData.direccion} onChange={handleChange} autoComplete="street-address" />
            </label>
            <label>
              Sexo
              <select name="sexo" value={formData.sexo} onChange={handleChange}>
                <option value="">Prefiero no indicar</option>
                <option value="Femenino">Femenino</option>
                <option value="Masculino">Masculino</option>
                <option value="Otro">Otro</option>
              </select>
            </label>

            <button type="submit" className={styles['btn-join-form']} disabled={loading}>
              {loading ? 'Registrando...' : 'Registrarme'}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default JoinFamilyPage;